import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Checkbox, IconButton, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { collaborationPlan as c } from '@/styles/tokens';
import { CollaborationStep } from '@/services/collaboration-plan.service';
import { COLLABORATION_STEP_VISUALS } from './collaborationStepIcons';
import { resolveActionNavigation } from './collaborationStepActions';

const checkboxBoxSx = { width: 16, height: 16, borderRadius: '3px', boxSizing: 'border-box' } as const;

const formatScheduled = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  const date = d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  return `${date} • ${time}`;
};

// Прострочена - заплановано в минулому, і ще не виконана (done-задачі простроченими не показуємо).
const isOverdue = (item: CollaborationStep): boolean =>
  item.status === 'pending' && !!item.scheduledLdt && new Date(item.scheduledLdt) < new Date();

interface CollaborationPlanRowProps {
  item: CollaborationStep;
  isLast?: boolean;
  /** Для тексту підтвердження видалення ("...з плану співпраці з [Ім'я_клієнта]?"). */
  clientName: string;
  onComplete: (stepId: number) => void;
  onDelete: (stepId: number) => void;
  onEdit?: (stepId: number) => void;
}

/** Один рядок задачі "Плану співпраці" - спільний для компактної картки на дашборді
 *  (`CollaborationPlanCard`) і бокової панелі "всі задачі" (`CollaborationPlanAllTasksPanel`),
 *  щоб не дублювати верстку рядка в двох місцях.
 *
 *  Олівець (редагування) - завжди, незалежно від типу. Стрілка "перейти до дії" - лише коли бек
 *  віддав `actionIntent` (не null) для цього кроку - фронт не знає, які типи мають дію, лише
 *  виконує намір (`collaborationStepActions.ts`). Мок-задачі (`item.clientId === null`) нікуди не
 *  ведуть - немає реального клієнта для маршруту. */
export const CollaborationPlanRow: React.FC<CollaborationPlanRowProps> = ({ item, isLast, clientName, onComplete, onDelete, onEdit }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.collaborationPlan' });
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const done = item.status === 'done';
  const visual = COLLABORATION_STEP_VISUALS[item.type];
  const scheduled = formatScheduled(item.scheduledLdt);
  const overdue = isOverdue(item);
  const title = item.type === 'custom' ? item.title ?? '' : t(`step.${item.type}`);
  const handleAction = () => {
    if (!item.actionIntent || item.clientId == null) return;
    const url = resolveActionNavigation(item.actionIntent, item.clientId);
    if (url) navigate(url);
  };
  const handleConfirmDelete = () => { setConfirmOpen(false); onDelete(item.id); };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 56, borderBottom: isLast ? 'none' : `1px solid ${c.divider}` }}>
      <Checkbox
        checked={done}
        disabled={done}
        onChange={() => onComplete(item.id)}
        inputProps={{ 'aria-label': title }}
        icon={<Box sx={{ ...checkboxBoxSx, border: `1.5px solid ${c.checkboxBorder}`, bgcolor: c.background }} />}
        sx={{ p: '8px', borderRadius: 0, flexShrink: 0, '&.Mui-checked': { color: c.checkboxChecked }, '& .MuiSvgIcon-root': { fontSize: 20 } }}
      />

      <Box sx={{ width: 28, height: 28, borderRadius: '4px', bgcolor: visual.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <visual.Icon sx={{ fontSize: 16, color: visual.color }} />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, py: '8px' }}>
        <Typography
          noWrap
          sx={{ fontSize: 14, lineHeight: '16px', color: c.text, textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.6 : 1 }}
        >
          {title}
        </Typography>
        {scheduled && (
          <Typography
            sx={{
              fontSize: 11, lineHeight: '13px', mt: '2px',
              color: overdue ? c.dateOverdue : c.dateCaption,
              fontWeight: overdue ? 500 : 400,
            }}
          >
            {scheduled}
          </Typography>
        )}
      </Box>

      {item.actionIntent && (
        <IconButton size="small" aria-label={t('goToAction')} onClick={handleAction} sx={{ color: c.caption, flexShrink: 0 }}>
          <ArrowForwardIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton size="small" aria-label={t('edit')} onClick={() => onEdit?.(item.id)} sx={{ color: c.caption, flexShrink: 0 }}>
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label={t('delete')}
        onClick={() => setConfirmOpen(true)}
        sx={{ color: c.caption, flexShrink: 0, '&:hover': { color: c.dateOverdue, bgcolor: 'transparent' } }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title')}
        message={t('deleteConfirm.message', { task: title, client: clientName })}
        confirmText={t('delete')}
        danger
      />
    </Box>
  );
};
